import React, { useState, useRef } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function fileIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['jpg','jpeg','png','gif','webp','heic'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['zip','rar','7z'].includes(ext)) return '🗜️';
  return '📎';
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

export default function PiecesJointes() {
  const [query, setQuery] = useState('');
  const [dossier, setDossier] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(''); setDossier(null); setFiles([]);
    try {
      const res = await api.get(`/admin/pj/dossier/${query.trim()}`);
      setDossier(res.data.dossier);
      setFiles(res.data.files || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Dossier introuvable. Vérifiez l\'ID Zoho.');
    } finally { setLoading(false); }
  };

  const doUpload = async (fileList) => {
    if (!dossier || !fileList?.length) return;
    setUploading(true); setError('');
    const fd = new FormData();
    Array.from(fileList).forEach(f => fd.append('files', f));
    try {
      const res = await api.post(`/admin/pj/upload/${dossier.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFiles(res.data.files || []);
      setMsg(`${res.data.count} fichier(s) ajouté(s)`);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'upload');
    } finally { setUploading(false); }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;
    setDeleting(filename); setError('');
    try {
      const res = await api.delete(`/admin/pj/delete/${dossier.id}/${encodeURIComponent(filename)}`);
      setFiles(res.data.files || []);
      setMsg('Fichier supprimé');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally { setDeleting(null); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    doUpload(e.dataTransfer.files);
  };

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Pièces jointes</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Documents par dossier</div>
      </div>

      <div className="page">
        {msg && <div className="alert alert-success" onClick={() => setMsg('')}>{msg} ✕</div>}

        {/* Recherche */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--text)' }}>
            Sélectionner un dossier
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
            Entrez la référence commande (O...), la référence Work Order (W...) ou l'ID Zoho numérique du dossier.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              className="form-input"
              style={{ flex: 1 }}
              placeholder="Ex: O260702_75202 ou W260702_84163 ou ID Zoho"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
            />
            <button className="btn btn-primary" onClick={search} disabled={loading || !query.trim()}>
              {loading ? '…' : 'Charger'}
            </button>
          </div>
          {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}
        </div>

        {/* Dossier trouvé */}
        {dossier && (
          <>
            {/* Infos dossier */}
            <div className="card" style={{ marginBottom: 20, background: '#EFF6FF', border: '1px solid #B8D4F0' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ fontSize: 28 }}>📁</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#185FA5' }}>{dossier.ref_commande}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>
                    {dossier.client_final}{dossier.ville ? ` — ${dossier.ville}` : ''} · {dossier.typo || 'Standard'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>ID : {dossier.id}</div>
                </div>
              </div>
            </div>

            {/* Zone upload */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Ajouter des fichiers</div>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#185FA5' : 'var(--border)'}`,
                  borderRadius: 12,
                  padding: '32px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: dragOver ? '#EFF6FF' : 'var(--bg2)',
                  transition: 'all .15s',
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
                <div style={{ fontWeight: 600, color: 'var(--text2)', fontSize: 14 }}>
                  {uploading ? 'Envoi en cours…' : 'Glisser-déposer ou cliquer pour sélectionner'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  PDF, images, Excel, Word… (25 Mo max par fichier)
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => doUpload(e.target.files)}
              />
            </div>

            {/* Liste des fichiers */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  {files.length} pièce{files.length !== 1 ? 's' : ''} jointe{files.length !== 1 ? 's' : ''}
                </span>
              </div>
              {files.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
                  <div>Aucune pièce jointe pour ce dossier</div>
                </div>
              ) : (
                <div>
                  {files.map((f, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '14px 20px',
                      borderBottom: i < files.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <span style={{ fontSize: 24, flexShrink: 0 }}>{fileIcon(f.name)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                          {formatSize(f.size)} · Ajouté le {new Date(f.date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(f.name)}
                        disabled={deleting === f.name}
                        style={{ flexShrink: 0 }}
                      >
                        {deleting === f.name ? '…' : 'Supprimer'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
